import { Component, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../../services/account/auth.service';
import { AuthRoles, AuthRolesFromString } from '../../models/enums/enums';
import { LoadPortalMenus } from '../../states/menu/menu.actions';
import { UpdateUser } from '../../states/user/user-action';
import { PartnerPortalTopNavBarComponent } from './top-nav-bar/top-nav-bar.component';
import { SignalRService } from '../../services/signal-r/signal-r.service';
import { NotificationFactoryService } from '../../services/notification/notification-factory.service';
import { PushNotificationService, NotificationData } from '../../services/notification/push-notification.service';
import { MessageService, SharedModule } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-partner-portal-layout',
  standalone: true,
  imports: [RouterOutlet, PartnerPortalTopNavBarComponent, ToastModule, AvatarModule, ButtonModule, CommonModule, SharedModule],
  providers: [MessageService],
  templateUrl: './partner-portal-layout.component.html',
  styleUrl: './partner-portal-layout.component.scss'
})
export class PartnerPortalLayoutComponent implements OnInit {
  constructor(
    private authService: AuthService,
    private store: Store,
    private signalRService: SignalRService,
    private notificationFactoryService: NotificationFactoryService,
    private pushNotificationService: PushNotificationService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new LoadPortalMenus());
    this.getUser();
    this.setSignalRConnection();
    this.observeNotification();
    this.getNotification();
  }

  observeNotification() {
    this.pushNotificationService.notification$.subscribe({
      next: (notification: NotificationData) => {
        this.messageService.add({
          key: 'notification',
          sticky: true,
          severity: 'success',
          summary: notification.message,
          data: {
            name: notification.name,
            image: notification.image,
            route: notification.route
          }
        });
      },
      error: (error) => {
        console.warn('Error fetching notification');
      }
    });
  }

  getNotification() {
    this.signalRService.hubNotification.subscribe({
      next: (response) => {
        var notificationService = this.notificationFactoryService.getNotificationService(response.messageTemplate);
        notificationService.notify(response.message, response.data);
      },
      error: (error) => {
        console.warn('Error fetching notification');
      }
    });
  }

  setSignalRConnection() {
    this.signalRService.initiateSignalrConnection().then(() => {
      console.log('SignalR connected');
    }).catch(err => {
      console.error('SignalR connection error: ', err);
    });
  }

  openNotification(route: string) {
    this.router.navigate([route]);
  }

  private getUser(): void {
    const accountData = localStorage.getItem('account');
    if (!accountData) {
      return;
    }

    const account = JSON.parse(accountData);
    const roles = this.getRoles(account.token);
    this.authService.getUserByid(account.userId).subscribe({
      next: (user) => {
        this.store.dispatch(
          new UpdateUser({
            id: user.id,
            email: user.email,
            phoneNumber: user.phoneNumber,
            firstName: user.firstName,
            lastName: user.lastName,
            role: roles,
            isNewUser: account.isNewUser,
            profileImageUrl: account.profileImageUrl,
            profileImageFormat: account.profileImageFormat
          })
        );
      },
      error: () => {}
    });
  }

  private getRoles(token: string): AuthRoles[] {
    if (!token) {
      return [];
    }

    try {
      const decodedToken: { [key: string]: unknown } = jwtDecode(token);
      let roles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] as string | string[] | undefined;
      if (typeof roles === 'string') {
        roles = [roles];
      } else if (!Array.isArray(roles)) {
        roles = [];
      }
      return roles.map((role: string) => AuthRolesFromString[role]).filter(Boolean);
    } catch {
      return [];
    }
  }
}
